/**
 * ContactDetailPageNotFound
 *
 * Empty-state shown when contact data has finished loading but came back
 * `null` — typically because the contact id is invalid, was deleted, or
 * the auth user doesn't have access. Pure / display-only with no
 * callbacks beyond an optional retry hook.
 */
import { cn } from '../cn';
import Button from '../Input2/Button';
import { Alerts } from '../StrokeIcons';

export type ContactDetailPageNotFoundProps = {
  /** Optional retry callback. When provided, surfaces a "Try again" button. */
  onRetry?: () => void;
  className?: string;
};

export function ContactDetailPageNotFound({ onRetry, className }: ContactDetailPageNotFoundProps) {
  return (
    <div
      data-testid="contact-detail-not-found"
      className={cn(
        'flex h-full flex-col items-center justify-center gap-3 px-6 py-12 text-center',
        className,
      )}>
      <Alerts.AlertCircle
        aria-hidden
        className="size-10 text-lynch-400 dark:text-lynch-500"
      />
      <div className="flex flex-col gap-1">
        <h2 className="text-lg font-semibold text-lynch-900 dark:text-lynch-100">
          Contact not found
        </h2>
        <p className="max-w-md text-sm text-lynch-600 dark:text-lynch-400">
          We couldn't load this contact. It may have been deleted, or you might not
          have access to it on the current team.
        </p>
      </div>
      {onRetry != null && (
        <Button
          data-testid="contact-detail-not-found-retry"
          color="blue" tone="outline"
          size="sm"
          onClick={onRetry}>
          Try again
        </Button>
      )}
    </div>
  );
}
